import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  DrawerBottomSheet,
  DrawerBottomSheetContent,
  DrawerBottomSheetFooter,
  DrawerBottomSheetHeader,
  DrawerBottomSheetHeaderCloseIcon,
  DrawerBottomSheetHeaderTitle,
  Flex,
  useDrawer,
} from '@edreamsodigeo/prisma-design-system';
import { useTranslation } from '@edreamsodigeo/frontend-core';
import { AutoRenewal } from '@/components/AutoRenewal';
import { PrimeLogoLong } from '../PrimeLogoLong/PrimeLogoLong';
import { ACTIONS, usePFTracking } from '@/hooks/tracking';
import {
  PF_AUTO_RENEWAL,
  usePriceFreezeSession,
} from '@/hooks/usePriceFreezeSession';

const DRAWER_TRANSITION_DURATION = 300;

export const AutorenewalDrawer = ({
  showRenewalDrawer = false,
  setShowRenewalDrawer,
  depositPrice,
  currency,
  subscriptionExpirationDate,
  onPrimaryAction = () => {},
  onSecondaryAction = () => {},
  onClose = () => {},
}) => {
  const { track } = usePFTracking();
  const [, setSessionItem] = usePriceFreezeSession();
  const { state, transitionDuration } = useDrawer({
    isOpen: showRenewalDrawer,
    transitionDuration: DRAWER_TRANSITION_DURATION,
  });

  const { t } = useTranslation();

  const translations = useMemo(
    () => ({
      primaryButton: t('pricefreeze.autorenewal.button.prime', {
        primeKey: t('variables.prime.plan.primebasic'),
      }),
      secondaryButton: t('pricefreeze.autorenewal.button.nonPrime'),
      closeAriaLabel: t('common.close', 'Close drawer'),
    }),
    [t]
  );

  const handleClose = () => {
    onClose?.();
    setShowRenewalDrawer(false);
  };

  const handlePrimaryAction = () => {
    onPrimaryAction?.();
    setShowRenewalDrawer(false);
    track(ACTIONS.AUTO_RENEWAL.CLICK.STAY_PRIME);
  };

  const handleSecondaryAction = () => {
    onSecondaryAction?.();
    setShowRenewalDrawer(false);
    track(ACTIONS.AUTO_RENEWAL.CLICK.GIVE_UP_MY_BENEFITS);
    setSessionItem(PF_AUTO_RENEWAL, 'false');
  };

  return (
    <DrawerBottomSheet
      state={state}
      transitionDuration={transitionDuration}
      onBackdropClick={handleClose}
      fullHeight
      sx={{ zIndex: 499 }}
      backDrop={null}
    >
      <PrimeHeader onClose={handleClose} />
      <DrawerBottomSheetContent>
        <AutoRenewal
          subscriptionExpirationDate={subscriptionExpirationDate}
          depositPrice={depositPrice}
          currency={currency}
        />
      </DrawerBottomSheetContent>
      <DrawerBottomSheetFooter alignContent={'center'}>
        <ActionButtons
          primaryText={translations.primaryButton}
          secondaryText={translations.secondaryButton}
          onPrimary={handlePrimaryAction}
          onSecondary={handleSecondaryAction}
        />
      </DrawerBottomSheetFooter>
    </DrawerBottomSheet>
  );
};

AutorenewalDrawer.propTypes = {
  showRenewalDrawer: PropTypes.bool,
  depositPrice: PropTypes.number,
  currency: PropTypes.string,
  subscriptionExpirationDate: PropTypes.string,
  onPrimaryAction: PropTypes.func,
  onSecondaryAction: PropTypes.func,
  onClose: PropTypes.func,
};

const PrimeHeader = ({ onClose }) => {
  const { track } = usePFTracking();
  return (
    <DrawerBottomSheetHeader
      alignContent={'center'}
      data-testid="drawer-header"
    >
      <DrawerBottomSheetHeaderCloseIcon
        placement={'start'}
        ariaLabel="Close drawer"
        onClick={() => {
          onClose();
          track(ACTIONS.AUTO_RENEWAL.CLICK.CLOSE);
        }}
        color="primary.50"
        sx={{ height: '18px', width: '18px' }}
      />
      <DrawerBottomSheetHeaderTitle>
        <PrimeLogoLong extraProps={{ height: '18px' }} />
      </DrawerBottomSheetHeaderTitle>
    </DrawerBottomSheetHeader>
  );
};

PrimeHeader.propTypes = {
  onClose: PropTypes.func.isRequired,
};

const ActionButtons = ({
  primaryText,
  secondaryText,
  onPrimary,
  onSecondary,
}) => (
  <Flex gap="1x_small" flexDirection="column" pt="1x_small" width="100%">
    <Button type="primary" onClick={onPrimary}>
      {primaryText}
    </Button>
    <Button type="text" onClick={onSecondary}>
      {secondaryText}
    </Button>
  </Flex>
);

ActionButtons.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
  onPrimary: PropTypes.func.isRequired,
  onSecondary: PropTypes.func.isRequired,
};
